import React, { useState, useRef } from "react"
import { Form, Input, Button, Checkbox, Flex } from "antd"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Title, Text } from "ui/Typography"
import { CustomInput, PhoneInput } from "ui/Input"
import { spacings } from "themes/variables"
import { CustomForm } from "ui/Form"
import { signUp } from "store/auth"
import { TermsAndConditions } from "./TermsAndConditions"
import { PrivacyPolicy } from "./PrivacyPolicy"
import { routes } from "router"
import ReCAPTCHA from "react-google-recaptcha"
import { Modal } from "ui/Modal"
import { CustomInputError } from "ui/CustomInputError"
import useFormErrors from "hooks/useAntdFormErrors"
import { getRole, isProApp } from "utils/app-type"
import { validateStrongPassword } from "../utils"
import EmailExistModal from "./EmailExistModal/EmailExistModal"
import { resetErrors } from "modules/errors/reducer"

export const SignUp = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading } = useSelector((state) => state.auth)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [isEmailExistModalOpen, setIsEmailExistModalOpen] = useState(false)
  const [form] = Form.useForm()
  const recaptchaRef = useRef()
  const role = getRole()
  const isProHost = isProApp()

  const showModal = (type) => {
    setModalType(type)
    setIsModalOpen(true)
  }
  const handleCancel = () => {
    setModalType(null)
    setIsModalOpen(false)
  }
  const { errors } = useFormErrors(form)

  const onFinish = (user) => {
    console.log(user, "userdetails")
    user.is_sportsman = true
    user.password_confirmation = user.password
    user.role = role
    user.redirect_url = "/auth/sign-up/confirm-email"

    const token = recaptchaRef.current.getValue()
    recaptchaRef.current.reset()
    user.captcha_token = token
    const payload = {
      ...user,
      phone_number: user.phone_number?.fullNumber,
      country_code: user.phone_number?.countryCode
    }

    dispatch(signUp(payload))
      .then((res) => {
        navigate(routes.confirmEmail({}, { email: user.email, phone_number: user.phone_number, user_id: res?.user?.id }))
      })
      .catch((err) => {
        if (err?.status === 400 && err?.data?.already_taken) {
          setIsEmailExistModalOpen(true)
        }
      })
  }

  const handleCancelEmailExistModal = () => {
    setIsEmailExistModalOpen(false)
  }

  const handleYesEmailExistModal = () => {
    setIsEmailExistModalOpen(false)
    dispatch(resetErrors())
    navigate(routes.signInV2(), { state: { email: form.getFieldValue("email") } })
  }

  return (
    <>
      <Title level={2} className="justify-center mb-3xl">
        {isProHost ? t("global.become_a_ds_pro") : t("sign_up.register_guest_account")}
      </Title>
      <CustomForm form={form} name="sign-up-email" layout="vertical" onFinish={onFinish}>
        <Flex gap={spacings.S}>
          <Form.Item
            label={t("sign_up.first_name")}
            name="first_name"
            required={true}
            rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
            className="flex-grow-1"
          >
            <CustomInput placeholder={t("sign_up.name")} />
          </Form.Item>
          <Form.Item
            label={t("sign_up.last_name")}
            name="last_name"
            required={true}
            rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
            className="flex-grow-1"
          >
            <CustomInput placeholder={t("sign_up.last_name")} />
          </Form.Item>
        </Flex>
        {isProHost && (
          <Form.Item label={t("sign_up.company")} name="company_name" className="flex-grow-1">
            <CustomInput placeholder={t("sign_up.company")} />
          </Form.Item>
        )}
        <Form.Item
          label={t("sign_up.email")}
          name="email"
          required={true}
          rules={[
            { required: true, message: t("form_validation.cannot_be_blank") },
            { type: "email", message: t("form_validation.invalid_email") }
          ]}
          help={errors?.email ? <CustomInputError errorKey={errors.email} /> : null}
        >
          <CustomInput placeholder={t("sign_up.email_placeholder")} />
        </Form.Item>
        <Form.Item
          label={t("sign_up.phone_number")}
          name="phone_number"
          required={isProHost}
          rules={[
            {
              validator: (_, value) => {
                if (!value?.fullNumber) {
                  return Promise.reject(t("form_validation.phone_required"))
                }
                return validatePhoneNumber(value.fullNumber)
              }
            }
          ]}
          help={errors?.phone_number ? <CustomInputError errorKey={errors.phone_number} /> : null}
        >
          <PhoneInput onChange={(value) => form.setFieldsValue({ phone_number: value })} />
        </Form.Item>
        <Form.Item
          label={t("sign_up.password")}
          name="password"
          required={true}
          rules={[
            {
              validator: (_, value) => validateStrongPassword(value, t)
            }
          ]}
        >
          <Input.Password placeholder={t("sign_up.password_placeholder")} />
        </Form.Item>
        <Form.Item
          name="captcha_token"
          help={errors?.recaptcha ? <CustomInputError errorKey="user.attributes.recaptcha.incorrect" /> : null}
        >
          <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_SITE_KEY} />
        </Form.Item>
        <Form.Item
          name="agreeCheck"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error(t("form_validation.accept_terms_conditions"))))
            }
          ]}
        >
          <Checkbox>
            <Text type="secondary" variant="body">
              {t("sign_up.agree_terms")}
            </Text>
            <Text className="ml-xs" link>
              <Link onClick={() => showModal("TermsAndConditions")}>{t("sign_up.terms_conditions")}</Link>
            </Text>
            <Text className="ml-xs" link>
              &
            </Text>
            <Text className="ml-xs" link>
              <Link onClick={() => showModal("PrivacyPolicy")}>{t("sign_up.privacy_policy")}</Link>
            </Text>
          </Checkbox>
        </Form.Item>
        {errors?.base ? <CustomInputError errorKey={errors.base} /> : null}
        <Form.Item>
          <Button className="mt-xs mb-xs" type="primary" htmlType="submit" block size="large" disabled={loading}>
            {t("sign_up.register")}
          </Button>
        </Form.Item>
      </CustomForm>
      <Flex justify="center">
        <Text type="secondary" variant="body">
          {t("sign_up.already_have_account")}
        </Text>
        <Text className="ml-xs" link>
          <Link to={routes.signInV2()}>{isProHost ? t("sign_in.log_in") : t("sign_in.log_in_as_guest")}</Link>
        </Text>
      </Flex>
      <Modal
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={1022}
        style={{
          top: 40
        }}
      >
        {modalType === "TermsAndConditions" ? <TermsAndConditions /> : <PrivacyPolicy />}
      </Modal>
      {isEmailExistModalOpen && (
        <EmailExistModal
          isModalOpen={isEmailExistModalOpen}
          handleCancel={handleCancelEmailExistModal}
          handleYesEmailExistModal={handleYesEmailExistModal}
        />
      )}
    </>
  )
}
